import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { Plus, Trash2, ChevronDown, ChevronUp, Layers, Copy as CopyIcon, Merge } from "lucide-react";
import TestStructureService from "../../_services/TestStructureService";

interface Lookup { id: number; name: string; }

interface StandardTestRule {
  config?: number;
  _comment?: string;
  courseType2Ids?: number[];
  linkTo?: "COURSE" | "TERM";
  categories?: number[];
  testType?: number;
  testKeyword?: number;
  testName?: string;
  centers?: number[];
  centerTags?: number[];
  sections?: Array<{ name?: string; marks?: string; passMark?: string; keyword?: number; time?: number }>;
}

interface ConsolidationRule {
  targetCategoryId: string;
  mergeLogic?: string;
  /**
   * Controls which merge flow the per-cell "Merge" button on the test-structure
   * grid dispatches to when an admin clicks it:
   *   "merge"  → backend test-structure mergeTest (runs the merge in-place)
   *   "merge2" → opens the Quick Quiz Maker merge-tests wizard in a new tab
   *              with source test IDs prefilled and target category locked.
   * The button label is always "Merge" — only the click behaviour differs.
   * Stored alongside the rule under sourceCategoriesForConsolidation.mergeStyle.
   */
  mergeStyle?: "merge" | "merge2";
  sources: Array<{ id: string }>;
  childRoundingRequired?: boolean;
  parentRoundingRequired?: boolean;
  computeGrade?: boolean;
}

interface Props {
  show: boolean;
  onHide: () => void;
  programId: number;
  yearId: number;
}

export default function ConfigEditorModal({ show, onHide, programId, yearId }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [rawJson, setRawJson] = useState("");
  const [rules, setRules] = useState<StandardTestRule[]>([]);
  const [consolidations, setConsolidations] = useState<ConsolidationRule[]>([]);
  const [testCategoriesList, setTestCategoriesList] = useState<string[]>([]);

  const [courseType2s, setCourseType2s] = useState<Lookup[]>([]);
  const [categories, setCategories] = useState<Lookup[]>([]);
  const [keywords, setKeywords] = useState<Lookup[]>([]);
  const [testTypes, setTestTypes] = useState<Array<Lookup & { displayLabel?: string }>>([]);

  const [tab, setTab] = useState<string>("standard");
  const [collapsedRules, setCollapsedRules] = useState<Set<number>>(new Set());
  const [collapsedMerges, setCollapsedMerges] = useState<Set<number>>(new Set());

  // Borrow-from-year
  const [borrowYears, setBorrowYears] = useState<Array<{ yearId: number; yearName: string }>>([]);
  const [borrowFromYearId, setBorrowFromYearId] = useState<number | "">("");
  const [borrowLoading, setBorrowLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setMsg(null);
    setLoading(true);
    (async () => {
      try {
        const data = await TestStructureService.getProgramConfig(programId, yearId);
        setCourseType2s(data.courseType2s || []);
        setCategories(data.categories || []);
        setKeywords(data.keywords || []);
        setTestTypes(data.testTypes || []);
        const cfg = data.config || {};
        setRawJson(JSON.stringify(cfg, null, 2));
        setRules(Array.isArray(cfg.standardTestConfig) ? cfg.standardTestConfig : []);
        setTestCategoriesList(Array.isArray(cfg.testCategories) ? cfg.testCategories.map(String) : []);
        // Parse testCategory consolidation rules
        const tc = cfg.testCategory || {};
        const cons: ConsolidationRule[] = [];
        for (const catId of Object.keys(tc)) {
          const c = tc[catId] || {};
          const src = c?.sourceCategoriesForConsolidation;
          if (src && Array.isArray(src.source)) {
            cons.push({
              targetCategoryId: String(catId),
              mergeLogic: src.mergeLogic || "1",
              mergeStyle: src.mergeStyle === "merge" ? "merge" : "merge2",
              sources: src.source.map((s: any) => ({ id: String(s.id) })),
              childRoundingRequired: !!c.childRoundingRequired,
              parentRoundingRequired: !!c.parentRoundingRequired,
              computeGrade: !!src.computeGrade,
            });
          }
        }
        setConsolidations(cons);

        // Load years that have a config we can borrow from
        try {
          const years = await TestStructureService.getBorrowYears(programId, yearId);
          setBorrowYears(years || []);
        } catch { /* ignore */ }
        setBorrowFromYearId("");
      } catch (e: any) {
        setMsg({ type: "error", text: "Failed to load config" });
      } finally {
        setLoading(false);
      }
    })();
  }, [show, programId, yearId]);

  // Parse a config object into the form state
  const applyConfigToForm = (cfg: any) => {
    setRawJson(JSON.stringify(cfg || {}, null, 2));
    setRules(Array.isArray(cfg?.standardTestConfig) ? cfg.standardTestConfig : []);
    setTestCategoriesList(Array.isArray(cfg?.testCategories) ? cfg.testCategories.map(String) : []);
    const tc = cfg?.testCategory || {};
    const cons: ConsolidationRule[] = [];
    for (const catId of Object.keys(tc)) {
      const c = tc[catId] || {};
      const src = c?.sourceCategoriesForConsolidation;
      if (src && Array.isArray(src.source)) {
        cons.push({
          targetCategoryId: String(catId),
          mergeLogic: src.mergeLogic || "1",
          mergeStyle: src.mergeStyle === "merge" ? "merge" : "merge2",
          sources: src.source.map((s: any) => ({ id: String(s.id) })),
          childRoundingRequired: !!c.childRoundingRequired,
          parentRoundingRequired: !!c.parentRoundingRequired,
          computeGrade: !!src.computeGrade,
        });
      }
    }
    setConsolidations(cons);
  };

  const handleBorrowFromYear = async (fromYearId: number) => {
    if (!fromYearId) return;
    setBorrowLoading(true);
    setMsg(null);
    try {
      const data = await TestStructureService.getProgramConfig(programId, fromYearId);
      const cfg = data?.config || {};
      const isEmpty = !cfg || Object.keys(cfg).length === 0;
      const yearName = borrowYears.find((y) => y.yearId === fromYearId)?.yearName || fromYearId;
      if (isEmpty) {
        setMsg({ type: "error", text: `Year "${yearName}" has no config to borrow. Select another year.` });
        setBorrowLoading(false);
        return;
      }
      applyConfigToForm(cfg);

      // Summarise what was loaded so user knows where to look
      const ruleCount = Array.isArray(cfg.standardTestConfig) ? cfg.standardTestConfig.length : 0;
      const mergeCount = cfg.testCategory
        ? Object.values(cfg.testCategory).filter((c: any) => c?.sourceCategoriesForConsolidation?.source?.length > 0).length
        : 0;
      const catCount = Array.isArray(cfg.testCategories) ? cfg.testCategories.length : 0;

      const parts: string[] = [];
      parts.push(`${ruleCount} Standard Test Rule${ruleCount === 1 ? "" : "s"}`);
      parts.push(`${mergeCount} Merge Rule${mergeCount === 1 ? "" : "s"}`);
      if (catCount > 0) parts.push(`${catCount} allowed categories`);
      const summary = parts.join(", ");

      // Auto-switch to a tab that has content
      if (ruleCount === 0 && mergeCount > 0) setTab("merge");
      else if (ruleCount === 0 && mergeCount === 0) setTab("json");
      else setTab("standard");

      setMsg({
        type: ruleCount + mergeCount > 0 ? "success" : "error",
        text: `Loaded from "${yearName}": ${summary}. Review each tab, then click Save Config to apply to current year.`,
      });
    } catch (e: any) {
      setMsg({ type: "error", text: "Failed to load config from selected year" });
    }
    setBorrowLoading(false);
  };

  const addRule = () => {
    setRules([...rules, { config: rules.length + 1, linkTo: "COURSE", courseType2Ids: [], categories: [], testType: 2, sections: [{ name: "Section 1", marks: "100", passMark: "0", keyword: 0, time: 0 }], testName: "${courseName}_${categoryName}" }]);
  };
  const removeRule = (idx: number) => setRules(rules.filter((_, i) => i !== idx));
  const updateRule = (idx: number, patch: Partial<StandardTestRule>) => {
    setRules(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const updateRuleSection = (ruleIdx: number, secIdx: number, patch: any) => {
    const rule = rules[ruleIdx];
    const secs = [...(rule.sections || [])];
    secs[secIdx] = { ...secs[secIdx], ...patch };
    updateRule(ruleIdx, { sections: secs });
  };
  const addRuleSection = (ruleIdx: number) => {
    const rule = rules[ruleIdx];
    updateRule(ruleIdx, { sections: [...(rule.sections || []), { name: "Section", marks: "100", passMark: "0", keyword: 0, time: 0 }] });
  };
  const removeRuleSection = (ruleIdx: number, secIdx: number) => {
    const rule = rules[ruleIdx];
    updateRule(ruleIdx, { sections: (rule.sections || []).filter((_, i) => i !== secIdx) });
  };
  const toggleCollapse = (idx: number) => {
    setCollapsedRules((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const collapseAll = () => setCollapsedRules(new Set(rules.map((_, i) => i)));
  const expandAll = () => setCollapsedRules(new Set());
  const duplicateRule = (idx: number) => {
    const clone = JSON.parse(JSON.stringify(rules[idx]));
    clone._comment = (clone._comment || "Rule") + " (copy)";
    const next = [...rules];
    next.splice(idx + 1, 0, clone);
    setRules(next);
  };
  const ruleColors = ["#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16"];
  const getCourseType2Name = (id: number) => courseType2s.find((c) => c.id === id)?.name || id;
  const getCategoryName = (id: number | string) => categories.find((c) => String(c.id) === String(id))?.name || id;

  const toggleMergeCollapse = (idx: number) => {
    setCollapsedMerges((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const collapseAllMerges = () => setCollapsedMerges(new Set(consolidations.map((_, i) => i)));
  const expandAllMerges = () => setCollapsedMerges(new Set());
  const duplicateConsolidation = (idx: number) => {
    const clone = JSON.parse(JSON.stringify(consolidations[idx]));
    const next = [...consolidations];
    next.splice(idx + 1, 0, clone);
    setConsolidations(next);
  };
  const mergeLogicLabel = (logic: string) => {
    if (logic === "1") return "Sum";
    if (logic === "2") return "Weighted";
    if (logic === "3") return "Average";
    return logic;
  };

  const addConsolidation = () => {
    setConsolidations([...consolidations, { targetCategoryId: "", mergeLogic: "1", mergeStyle: "merge2", sources: [{ id: "" }], computeGrade: false }]);
  };
  const removeConsolidation = (idx: number) => setConsolidations(consolidations.filter((_, i) => i !== idx));
  const updateConsolidation = (idx: number, patch: Partial<ConsolidationRule>) => {
    setConsolidations(consolidations.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };

  const assembleConfig = () => {
    const base: any = {};
    if (testCategoriesList.length > 0) base.testCategories = testCategoriesList;
    if (rules.length > 0) base.standardTestConfig = rules;
    if (consolidations.length > 0) {
      base.testCategory = {};
      for (const c of consolidations) {
        if (!c.targetCategoryId) continue;
        base.testCategory[c.targetCategoryId] = {
          childRoundingRequired: !!c.childRoundingRequired,
          parentRoundingRequired: !!c.parentRoundingRequired,
          sourceCategoriesForConsolidation: {
            source: c.sources.filter(s => s.id).map(s => ({ id: s.id })),
            mergeLogic: c.mergeLogic || "1",
            mergeStyle: c.mergeStyle === "merge" ? "merge" : "merge2",
            computeGrade: !!c.computeGrade,
          },
        };
      }
    }
    return base;
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      let configToSave: any;
      if (tab === "json") {
        configToSave = JSON.parse(rawJson);
      } else {
        configToSave = assembleConfig();
      }
      const resp = await TestStructureService.saveProgramConfig(programId, yearId, configToSave);
      setMsg({ type: resp.success ? "success" : "error", text: resp.message });
      if (resp.success) {
        setRawJson(JSON.stringify(configToSave, null, 2));
      }
    } catch (e: any) {
      setMsg({ type: "error", text: e?.message || "Failed to save" });
    }
    setSaving(false);
  };

  const commonInputStyle: React.CSSProperties = { padding: "4px 8px", border: "1px solid var(--border-strong)", borderRadius: 4, fontSize: "0.8rem" };

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="ts-timetable-modal-full">
      <Modal.Header closeButton style={{ background: "var(--brand-accent)", color: "var(--text-on-brand)" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>Program Config Editor</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: "0.85rem" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner animation="border" /></div>
        ) : (
          <>
            {/* Borrow from Year bar */}
            {borrowYears.length > 0 && (
              <div style={{
                padding: "10px 14px", marginBottom: 12, borderRadius: 6,
                background: "var(--status-info-bg)", border: "1px solid #bfdbfe",
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              }}>
                <span style={{ fontWeight: 600, color: "var(--brand-primary-active)", fontSize: "0.82rem" }}>💡 Borrow config from another year:</span>
                <select
                  value={borrowFromYearId}
                  onChange={(e) => setBorrowFromYearId(e.target.value ? Number(e.target.value) : "")}
                  disabled={borrowLoading}
                  style={{ padding: "4px 8px", border: "1px solid #93c5fd", borderRadius: 4, fontSize: "0.82rem", minWidth: 160 }}
                >
                  <option value="">Select year…</option>
                  {borrowYears.map((y) => (
                    <option key={y.yearId} value={y.yearId}>{y.yearName}</option>
                  ))}
                </select>
                <button
                  onClick={() => borrowFromYearId && handleBorrowFromYear(Number(borrowFromYearId))}
                  disabled={!borrowFromYearId || borrowLoading}
                  style={{
                    padding: "5px 14px", borderRadius: 5, border: "none", cursor: borrowFromYearId ? "pointer" : "not-allowed",
                    background: borrowFromYearId ? "#2563eb" : "#cbd5e1", color: "var(--text-on-brand)",
                    fontSize: "0.78rem", fontWeight: 600,
                  }}>
                  {borrowLoading ? "Loading…" : "Load Config"}
                </button>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  Fills the form below. Click Save to apply.
                </span>
              </div>
            )}

            {/* Custom tab bar */}
            <div style={{ display: "flex", gap: 4, borderBottom: "2px solid var(--border-default)", marginBottom: 16 }}>
              {[
                { key: "standard", label: "Standard Test Rules", color: "var(--status-success)" },
                { key: "merge", label: "Merge / Consolidation", color: "var(--brand-primary)" },
                { key: "json", label: "Raw JSON", color: "var(--text-muted)" },
              ].map((t) => {
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)}
                    style={{
                      padding: "10px 20px",
                      border: "none",
                      borderBottom: active ? `3px solid ${t.color}` : "3px solid transparent",
                      marginBottom: "-2px",
                      background: active ? "#fff" : "transparent",
                      color: active ? t.color : "var(--text-muted)",
                      fontWeight: active ? 700 : 500,
                      fontSize: "0.88rem",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* ── Standard Test Rules ── */}
            {tab === "standard" && (
              <div>
                <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-muted)" }}>
                    Rules that generate tests per course × category when "Create Standard Tests" is clicked.
                    {rules.length > 0 && <strong style={{ marginLeft: 8, color: "var(--text-strong)" }}>{rules.length} rule{rules.length === 1 ? "" : "s"}</strong>}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    {rules.length > 1 && (
                      <>
                        <button onClick={collapseAll} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid var(--border-strong)", background: "var(--surface-card)", color: "var(--text-body)", fontSize: "0.75rem", cursor: "pointer" }}>
                          Collapse All
                        </button>
                        <button onClick={expandAll} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid var(--border-strong)", background: "var(--surface-card)", color: "var(--text-body)", fontSize: "0.75rem", cursor: "pointer" }}>
                          Expand All
                        </button>
                      </>
                    )}
                    <button onClick={addRule}
                      style={{ padding: "6px 14px", borderRadius: 5, border: "none", cursor: "pointer", background: "var(--status-success)", color: "var(--text-on-brand)", fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, boxShadow: "0 1px 3px rgba(5,150,105,0.3)" }}>
                      <Plus size={14} /> Add Rule
                    </button>
                  </div>
                </div>
                {rules.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: "var(--text-faint)", border: "2px dashed var(--border-default)", borderRadius: 10, background: "var(--surface-page)" }}>
                    <Layers size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                    <div style={{ fontSize: "0.9rem" }}>No rules configured yet.</div>
                    <div style={{ fontSize: "0.78rem", marginTop: 4 }}>Click <strong>+ Add Rule</strong> above to start.</div>
                  </div>
                ) : (
                  rules.map((r, ri) => {
                    const color = ruleColors[ri % ruleColors.length];
                    const collapsed = collapsedRules.has(ri);
                    const ruleName = r._comment || `Rule ${ri + 1}`;
                    const sectionCount = (r.sections || []).length;
                    const totalMarks = (r.sections || []).reduce((s, sec) => s + Number(sec.marks || 0), 0);
                    const categoryNames = (r.categories || []).map(getCategoryName).join(", ");
                    const courseType2Names = (r.courseType2Ids || []).map(getCourseType2Name).join(", ");

                    return (
                      <div key={ri} style={{
                        borderRadius: 10,
                        marginBottom: 14,
                        background: "var(--surface-card)",
                        boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.04)",
                        overflow: "hidden",
                        border: `1px solid ${color}30`,
                        borderLeft: `5px solid ${color}`,
                      }}>
                        {/* Header bar */}
                        <div
                          onClick={() => toggleCollapse(ri)}
                          style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "10px 14px",
                            background: `linear-gradient(to right, ${color}10, ${color}03)`,
                            cursor: "pointer",
                            borderBottom: collapsed ? "none" : `1px solid ${color}20`,
                          }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                            <span style={{
                              background: color, color: "var(--text-on-brand)",
                              padding: "3px 10px", borderRadius: 999,
                              fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.02em",
                              whiteSpace: "nowrap",
                            }}>
                              RULE #{ri + 1}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: "0.92rem", color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {ruleName}
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                              <span>📎 {r.linkTo || "COURSE"}</span>
                              {categoryNames && <span>📁 {categoryNames}</span>}
                              <span>📝 {sectionCount} section{sectionCount === 1 ? "" : "s"} · {totalMarks} marks</span>
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => duplicateRule(ri)} title="Duplicate rule"
                              style={{ background: "transparent", border: "none", color: "var(--brand-primary)", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                              <CopyIcon size={15} />
                            </button>
                            <button onClick={() => removeRule(ri)} title="Delete rule"
                              style={{ background: "transparent", border: "none", color: "var(--status-danger)", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                              <Trash2 size={15} />
                            </button>
                            <button onClick={() => toggleCollapse(ri)} title={collapsed ? "Expand" : "Collapse"}
                              style={{ background: "transparent", border: "none", color: "var(--text-body)", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                              {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                          </div>
                        </div>

                        {!collapsed && (
                          <div style={{ padding: 16 }}>
                            {/* Name / Comment */}
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Rule Name</label>
                              <input value={r._comment || ""} placeholder="e.g. Theory Mid-Term Exam"
                                style={{ ...commonInputStyle, width: "100%", fontWeight: 600, marginTop: 3 }}
                                onChange={(e) => updateRule(ri, { _comment: e.target.value })} />
                            </div>

                            {/* Row 1: Link, Type, Keyword */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 12 }}>
                              <div>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Link To</label>
                                <select style={{ ...commonInputStyle, width: "100%", marginTop: 3 }} value={r.linkTo || "COURSE"}
                                  onChange={(e) => updateRule(ri, { linkTo: e.target.value as any })}>
                                  <option value="COURSE">COURSE</option>
                                  <option value="TERM">TERM</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Test Type</label>
                                <select style={{ ...commonInputStyle, width: "100%", marginTop: 3 }} value={r.testType ?? ""}
                                  onChange={(e) => updateRule(ri, { testType: e.target.value ? Number(e.target.value) : undefined })}>
                                  <option value="">--</option>
                                  {testTypes.map((t) => (
                                    <option key={t.id} value={t.id}>{t.displayLabel || t.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Test Keyword</label>
                                <select style={{ ...commonInputStyle, width: "100%", marginTop: 3 }} value={r.testKeyword || ""}
                                  onChange={(e) => updateRule(ri, { testKeyword: Number(e.target.value) || undefined })}>
                                  <option value="">--</option>
                                  {keywords.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                                </select>
                              </div>
                            </div>

                            {/* Test Name Template */}
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Test Name Template</label>
                              <input style={{ ...commonInputStyle, width: "100%", marginTop: 3 }} value={r.testName || ""}
                                placeholder="${courseName}_${categoryName}"
                                onChange={(e) => updateRule(ri, { testName: e.target.value })} />
                              <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <span>Placeholders:</span>
                                {["${courseName}", "${categoryName}", "${programCode}", "${yearName}", "${termName}"].map((p) => (
                                  <code key={p} style={{ background: "var(--surface-sunken)", padding: "1px 6px", borderRadius: 3, color: "var(--text-body)" }}>{p}</code>
                                ))}
                              </div>
                            </div>

                            {/* Row 2: Course Type2 + Categories */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                              <div style={{ background: "var(--surface-page)", padding: 10, borderRadius: 6, border: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Applies To (Course Type 2)</label>
                                <div style={{ fontSize: "0.68rem", color: "var(--text-faint)", marginBottom: 4 }}>Hold Ctrl/Cmd to select multiple. Currently: <strong>{courseType2Names || "All"}</strong></div>
                                <select multiple style={{ ...commonInputStyle, width: "100%", height: 80, background: "var(--surface-card)" }} value={(r.courseType2Ids || []).map(String)}
                                  onChange={(e) => updateRule(ri, { courseType2Ids: Array.from(e.target.selectedOptions).map((o) => Number(o.value)) })}>
                                  {courseType2s.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                                </select>
                              </div>
                              <div style={{ background: "var(--surface-page)", padding: 10, borderRadius: 6, border: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Creates Tests Under Categories</label>
                                <div style={{ fontSize: "0.68rem", color: "var(--text-faint)", marginBottom: 4 }}>Currently: <strong>{categoryNames || "None"}</strong></div>
                                <select multiple style={{ ...commonInputStyle, width: "100%", height: 80, background: "var(--surface-card)" }} value={(r.categories || []).map(String)}
                                  onChange={(e) => updateRule(ri, { categories: Array.from(e.target.selectedOptions).map((o) => Number(o.value)) })}>
                                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                              </div>
                            </div>

                            {/* Sections */}
                            <div style={{ border: "1px solid var(--border-default)", borderRadius: 6, overflow: "hidden" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "var(--surface-page)", borderBottom: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-strong)" }}>
                                  Sections ({sectionCount}) — Total: {totalMarks} marks
                                </label>
                                <button onClick={() => addRuleSection(ri)}
                                  style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid var(--brand-primary)", background: "var(--surface-card)", color: "var(--brand-primary)", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600 }}>
                                  + Add Section
                                </button>
                              </div>
                              <table style={{ width: "100%", fontSize: "0.76rem", borderCollapse: "collapse" }}>
                                <thead>
                                  <tr style={{ background: "var(--surface-card)" }}>
                                    <th style={{ padding: "6px 10px", textAlign: "left", borderBottom: "1px solid var(--border-default)", fontWeight: 600, color: "var(--text-body)" }}>Name</th>
                                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", fontWeight: 600, color: "var(--text-body)" }}>Marks</th>
                                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", fontWeight: 600, color: "var(--text-body)" }}>Pass</th>
                                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", fontWeight: 600, color: "var(--text-body)" }}>Keyword</th>
                                    <th style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-default)", fontWeight: 600, color: "var(--text-body)" }}>Time (min)</th>
                                    <th style={{ borderBottom: "1px solid var(--border-default)" }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(r.sections || []).map((s, si) => (
                                    <tr key={si} style={{ borderBottom: "1px solid var(--border-subtle)", background: si % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                      <td style={{ padding: "5px 10px" }}><input style={{ ...commonInputStyle, width: "100%" }} value={s.name || ""}
                                        onChange={(e) => updateRuleSection(ri, si, { name: e.target.value })} /></td>
                                      <td style={{ padding: "5px 10px" }}><input style={{ ...commonInputStyle, width: 70 }} value={s.marks || ""}
                                        onChange={(e) => updateRuleSection(ri, si, { marks: e.target.value })} /></td>
                                      <td style={{ padding: "5px 10px" }}><input style={{ ...commonInputStyle, width: 60 }} value={s.passMark || ""}
                                        onChange={(e) => updateRuleSection(ri, si, { passMark: e.target.value })} /></td>
                                      <td style={{ padding: "5px 10px" }}><select style={{ ...commonInputStyle, width: 140 }} value={s.keyword || ""}
                                        onChange={(e) => updateRuleSection(ri, si, { keyword: Number(e.target.value) || 0 })}>
                                        <option value="">--</option>
                                        {keywords.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                                      </select></td>
                                      <td style={{ padding: "5px 10px" }}><input type="number" style={{ ...commonInputStyle, width: 70 }} value={s.time || 0}
                                        onChange={(e) => updateRuleSection(ri, si, { time: Number(e.target.value) })} /></td>
                                      <td style={{ padding: "5px 10px" }}>
                                        <button onClick={() => removeRuleSection(ri, si)} title="Remove section"
                                          style={{ background: "transparent", border: "none", color: "var(--status-danger)", cursor: "pointer" }}>
                                          <Trash2 size={14} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                  {sectionCount === 0 && (
                                    <tr>
                                      <td colSpan={6} style={{ padding: 12, textAlign: "center", color: "var(--text-faint)", fontSize: "0.78rem" }}>
                                        No sections. Click <strong>+ Add Section</strong> to add one.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ── Merge / Consolidation Rules ── */}
            {tab === "merge" && (
              <div>
                <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ color: "var(--text-muted)" }}>
                    Consolidate marks from source categories into a target summary category.
                    {consolidations.length > 0 && <strong style={{ marginLeft: 8, color: "var(--text-strong)" }}>{consolidations.length} rule{consolidations.length === 1 ? "" : "s"}</strong>}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    {consolidations.length > 1 && (
                      <>
                        <button onClick={collapseAllMerges} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid var(--border-strong)", background: "var(--surface-card)", color: "var(--text-body)", fontSize: "0.75rem", cursor: "pointer" }}>
                          Collapse All
                        </button>
                        <button onClick={expandAllMerges} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid var(--border-strong)", background: "var(--surface-card)", color: "var(--text-body)", fontSize: "0.75rem", cursor: "pointer" }}>
                          Expand All
                        </button>
                      </>
                    )}
                    <button onClick={addConsolidation}
                      style={{ padding: "6px 14px", borderRadius: 5, border: "none", cursor: "pointer", background: "var(--brand-primary)", color: "var(--text-on-brand)", fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, boxShadow: "0 1px 3px rgba(37,99,235,0.3)" }}>
                      <Plus size={14} /> Add Merge Rule
                    </button>
                  </div>
                </div>
                {consolidations.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: "var(--text-faint)", border: "2px dashed var(--border-default)", borderRadius: 10, background: "var(--surface-page)" }}>
                    <Merge size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                    <div style={{ fontSize: "0.9rem" }}>No merge rules configured yet.</div>
                    <div style={{ fontSize: "0.78rem", marginTop: 4 }}>Click <strong>+ Add Merge Rule</strong> above to start.</div>
                  </div>
                ) : (
                  consolidations.map((c, ci) => {
                    const color = ruleColors[ci % ruleColors.length];
                    const collapsed = collapsedMerges.has(ci);
                    const targetName = c.targetCategoryId ? getCategoryName(c.targetCategoryId) : "(none)";
                    const sourceNames = c.sources.filter((s) => s.id).map((s) => getCategoryName(s.id)).join(", ");
                    const sourceCount = c.sources.filter((s) => s.id).length;

                    return (
                      <div key={ci} style={{
                        borderRadius: 10,
                        marginBottom: 14,
                        background: "var(--surface-card)",
                        boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.04)",
                        overflow: "hidden",
                        border: `1px solid ${color}30`,
                        borderLeft: `5px solid ${color}`,
                      }}>
                        {/* Header bar */}
                        <div
                          onClick={() => toggleMergeCollapse(ci)}
                          style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            padding: "10px 14px",
                            background: `linear-gradient(to right, ${color}10, ${color}03)`,
                            cursor: "pointer",
                            borderBottom: collapsed ? "none" : `1px solid ${color}20`,
                          }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                            <span style={{
                              background: color, color: "var(--text-on-brand)",
                              padding: "3px 10px", borderRadius: 999,
                              fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.02em",
                              whiteSpace: "nowrap",
                            }}>
                              MERGE #{ci + 1}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: "0.92rem", color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {sourceNames || "(no sources)"} → <span style={{ color }}>{targetName}</span>
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                              <span>⚙ {mergeLogicLabel(c.mergeLogic || "1")}</span>
                              <span>📊 {sourceCount} source{sourceCount === 1 ? "" : "s"}</span>
                              {c.computeGrade && <span>🎓 Grade</span>}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => duplicateConsolidation(ci)} title="Duplicate rule"
                              style={{ background: "transparent", border: "none", color: "var(--brand-primary)", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                              <CopyIcon size={15} />
                            </button>
                            <button onClick={() => removeConsolidation(ci)} title="Delete rule"
                              style={{ background: "transparent", border: "none", color: "var(--status-danger)", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                              <Trash2 size={15} />
                            </button>
                            <button onClick={() => toggleMergeCollapse(ci)} title={collapsed ? "Expand" : "Collapse"}
                              style={{ background: "transparent", border: "none", color: "var(--text-body)", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                              {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            </button>
                          </div>
                        </div>

                        {!collapsed && (
                          <div style={{ padding: 16 }}>
                            {/* Row 1: Target, Logic, Merge Style */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                              <div style={{ background: "var(--surface-page)", padding: 10, borderRadius: 6, border: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Target Category *</label>
                                <div style={{ fontSize: "0.68rem", color: "var(--text-faint)", marginBottom: 4 }}>Receives the consolidated result</div>
                                <select style={{ ...commonInputStyle, width: "100%", background: "var(--surface-card)" }} value={c.targetCategoryId}
                                  onChange={(e) => updateConsolidation(ci, { targetCategoryId: e.target.value })}>
                                  <option value="">-- Select Target --</option>
                                  {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                </select>
                              </div>
                              <div style={{ background: "var(--surface-page)", padding: 10, borderRadius: 6, border: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Merge Logic</label>
                                <div style={{ fontSize: "0.68rem", color: "var(--text-faint)", marginBottom: 4 }}>How source marks combine</div>
                                <select style={{ ...commonInputStyle, width: "100%", background: "var(--surface-card)" }} value={c.mergeLogic || "1"}
                                  onChange={(e) => updateConsolidation(ci, { mergeLogic: e.target.value })}>
                                  <option value="1">1 — Sum</option>
                                  <option value="2">2 — Weighted</option>
                                  <option value="3">3 — Average</option>
                                </select>
                              </div>
                              <div style={{ background: "var(--surface-page)", padding: 10, borderRadius: 6, border: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Merge Style</label>
                                <div style={{ fontSize: "0.68rem", color: "var(--text-faint)", marginBottom: 4 }}>What the "Merge" button does</div>
                                <select style={{ ...commonInputStyle, width: "100%", background: "var(--surface-card)" }} value={c.mergeStyle || "merge2"}
                                  onChange={(e) => updateConsolidation(ci, { mergeStyle: (e.target.value === "merge" ? "merge" : "merge2") })}>
                                  <option value="merge2">Merge2 — open wizard with prefilled IDs (new tab)</option>
                                  <option value="merge">Merge — run server-side merge in place</option>
                                </select>
                              </div>
                            </div>

                            {/* Options checkboxes */}
                            <div style={{ display: "flex", gap: 16, marginBottom: 12, padding: "8px 10px", background: "var(--surface-page)", borderRadius: 6, border: "1px solid var(--border-default)", flexWrap: "wrap" }}>
                              <label style={{ fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={!!c.childRoundingRequired}
                                  onChange={(e) => updateConsolidation(ci, { childRoundingRequired: e.target.checked })} />
                                <span>Child Rounding Required</span>
                              </label>
                              <label style={{ fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={!!c.parentRoundingRequired}
                                  onChange={(e) => updateConsolidation(ci, { parentRoundingRequired: e.target.checked })} />
                                <span>Parent Rounding Required</span>
                              </label>
                              <label style={{ fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                                <input type="checkbox" checked={!!c.computeGrade}
                                  onChange={(e) => updateConsolidation(ci, { computeGrade: e.target.checked })} />
                                <span>Compute Grade</span>
                              </label>
                            </div>

                            {/* Source Categories */}
                            <div style={{ border: "1px solid var(--border-default)", borderRadius: 6, overflow: "hidden" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "var(--surface-page)", borderBottom: "1px solid var(--border-default)" }}>
                                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-strong)" }}>
                                  Source Categories ({sourceCount}) — consolidated into <span style={{ color }}>{targetName}</span>
                                </label>
                                <button onClick={() => updateConsolidation(ci, { sources: [...c.sources, { id: "" }] })}
                                  style={{ padding: "3px 10px", borderRadius: 4, border: "1px solid var(--brand-primary)", background: "var(--surface-card)", color: "var(--brand-primary)", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600 }}>
                                  + Add Source
                                </button>
                              </div>
                              <div style={{ padding: 12 }}>
                                {c.sources.length === 0 ? (
                                  <div style={{ textAlign: "center", color: "var(--text-faint)", fontSize: "0.78rem", padding: 8 }}>
                                    No sources. Click <strong>+ Add Source</strong> to pick source categories.
                                  </div>
                                ) : (
                                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {c.sources.map((s, si) => (
                                      <div key={si} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--surface-card)", padding: "4px 4px 4px 0", border: "1px solid var(--border-default)", borderRadius: 6 }}>
                                        <select style={{ ...commonInputStyle, width: 200, border: "none", background: "transparent", paddingLeft: 8 }} value={s.id}
                                          onChange={(e) => {
                                            const srcs = [...c.sources];
                                            srcs[si] = { id: e.target.value };
                                            updateConsolidation(ci, { sources: srcs });
                                          }}>
                                          <option value="">-- Select Source --</option>
                                          {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                        </select>
                                        <button onClick={() => updateConsolidation(ci, { sources: c.sources.filter((_, i) => i !== si) })}
                                          title="Remove source"
                                          style={{ background: "transparent", border: "none", color: "var(--status-danger)", cursor: "pointer", padding: "0 6px" }}>
                                          <Trash2 size={13} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ── Raw JSON ── */}
            {tab === "json" && (
              <div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: 6 }}>
                Full <code>program_year_properties.Config</code> JSON. Edit carefully — invalid JSON will be rejected.
              </div>
              <textarea
                style={{ width: "100%", minHeight: 400, fontFamily: "monospace", fontSize: "0.8rem", padding: 8, border: "1px solid var(--border-strong)", borderRadius: 4 }}
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
              />
              </div>
            )}
          </>
        )}

        {msg && (
          <div style={{
            marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: "0.82rem",
            background: msg.type === "success" ? "#dcfce7" : "#fee2e2",
            color: msg.type === "success" ? "#166534" : "#991b1b",
          }}>{msg.text}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} style={{ backgroundColor: "#6b7280", border: "none" }}>Close</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || loading} style={{ backgroundColor: "var(--brand-primary)", border: "none" }}>
          {saving ? <Spinner animation="border" size="sm" /> : "Save Config"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
